import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import {
    createElement,
    FunctionComponent,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ResolvedWidget } from '@/pages/widgetEditor/types';
import {
    SAMPLE_DATA_ATTRIBUTE,
    buildAttributes,
    PageConfigElement,
} from '@/pages/widgetEditor/utils';
import { useFontLinks } from '@/pages/widgetEditor/hooks/useFontLinks';
import { useWidgetEntry } from '@/pages/widgetEditor/hooks/useWidgetEntry';

/** The empty host page in `public/`; see the comment in it. */
const PREVIEW_PAGE_URL = '/widget-preview.html';

// A widget's dialogs and menus open inside the frame, so a frame as short as a
// button would clip them.
const MIN_FRAME_HEIGHT = 480;

// Styled by `public/widget-preview.html`: the editor's theme does not reach the
// frame's document.
const ROW_CLASS = 'preview-row';
const LABEL_CLASS = 'preview-label';

type WidgetPreviewFrameProps = {
    resolvedWidgets: ResolvedWidget[];
    /** Absolute URL of the selected version's module entry. */
    entryUrl?: string;
    pageConfig?: PageConfigElement;
    /** Width of the frame's window, i.e. the viewport being previewed. */
    width: number;
};

/**
 * The widgets on their own page in an iframe, so they lay out for `width` as
 * they would in a browser window that wide. A viewport wider than the column is
 * scaled down to fit.
 *
 * Key it by `entryUrl`: element definitions are permanent per window, so only a
 * fresh frame can load another version.
 */
export const WidgetPreviewFrame: FunctionComponent<WidgetPreviewFrameProps> = ({
    resolvedWidgets,
    entryUrl,
    pageConfig,
    width,
}) => {
    const columnRef = useRef<HTMLDivElement>(null);
    const [frame, setFrame] = useState<Window | null>(null);
    const [columnWidth, setColumnWidth] = useState(width);
    const [contentHeight, setContentHeight] = useState(MIN_FRAME_HEIGHT);

    const usedTags = useMemo(
        () => resolvedWidgets.map(({ widget }) => widget.tag),
        [resolvedWidgets]
    );
    const scriptStateFor = useWidgetEntry(entryUrl, usedTags, frame);

    // Distinct font-url values across every instance, so the preview matches
    // what the generated snippet will load for a customer.
    const fontHrefs = useMemo(
        () =>
            Array.from(
                new Set(
                    resolvedWidgets
                        .map(
                            ({ widget, config }) =>
                                buildAttributes(widget, config)['font-url']
                        )
                        .filter((href): href is string => Boolean(href))
                )
            ),
        [resolvedWidgets]
    );
    useFontLinks(fontHrefs, frame?.document ?? null);

    useLayoutEffect(() => {
        const column = columnRef.current;
        if (!column) return;
        setColumnWidth(column.clientWidth);
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) =>
            setColumnWidth(entry.contentRect.width)
        );
        observer.observe(column);
        return () => observer.disconnect();
    }, []);

    // The frame grows with its page, so the editor scrolls, not the frame. The
    // frame's own ResizeObserver: the body is laid out in the frame's window.
    useEffect(() => {
        const FrameResizeObserver = (frame as typeof window | null)
            ?.ResizeObserver;
        if (!frame || !FrameResizeObserver) return;
        const { body } = frame.document;
        const observer = new FrameResizeObserver(() =>
            setContentHeight(
                Math.max(MIN_FRAME_HEIGHT, body.getBoundingClientRect().height)
            )
        );
        observer.observe(body);
        return () => observer.disconnect();
    }, [frame]);

    const scale = Math.min(1, columnWidth / width);
    const states = resolvedWidgets.map(({ widget }) => ({
        label: widget.label.de,
        state: scriptStateFor(widget.tag),
    }));
    const failed = states.filter(({ state }) => state === 'error');

    return (
        <>
            <Box ref={columnRef}>
                {failed.length > 0 && (
                    <Alert severity='error' sx={{ mb: 4 }}>
                        Vorschau konnte nicht geladen werden:{' '}
                        {failed.map(({ label }) => label).join(', ')}
                    </Alert>
                )}
                {/*
                 * A browser window at the scaled width: the bar names the viewport
                 * so a scaled-down desktop is not mistaken for a narrow layout.
                 */}
                <Box
                    sx={{
                        width: width * scale,
                        mx: 'auto',
                        overflow: 'hidden',
                        borderRadius: '12px',
                        bgcolor: 'common.white',
                        boxShadow: (theme) =>
                            `0 0 0 1px ${theme.palette.divider}, 0 8px 24px rgba(15, 23, 42, 0.06)`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            height: 36,
                            px: 3,
                            bgcolor: 'grey.50',
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        {[0, 1, 2].map((dot) => (
                            <Box
                                key={dot}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'grey.300',
                                }}
                            />
                        ))}
                        {states.some(({ state }) => state === 'loading') && (
                            <CircularProgress size={14} sx={{ ml: 2 }} />
                        )}
                        <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{
                                ml: 'auto',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {width} px
                            {scale < 1 && ` · ${Math.round(scale * 100)} %`}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            height: contentHeight * scale,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            component='iframe'
                            title='Widget-Vorschau'
                            src={PREVIEW_PAGE_URL}
                            onLoad={(event) =>
                                setFrame(
                                    (event.currentTarget as HTMLIFrameElement)
                                        .contentWindow
                                )
                            }
                            sx={{
                                display: 'block',
                                border: 0,
                                width,
                                height: contentHeight,
                                transform: `scale(${scale})`,
                                transformOrigin: 'top left',
                            }}
                        />
                    </Box>
                </Box>
            </Box>
            {/* Outside the Box: MUI's children check rejects a portal. */}
            {frame &&
                createPortal(
                    <>
                        {/*
                         * In the DOM before the entry script loads, which
                         * reads it once, just as the host page's own copy is.
                         */}
                        {pageConfig &&
                            createElement(
                                pageConfig.tag,
                                pageConfig.attributes
                            )}
                        {resolvedWidgets.map(({ instance, widget, config }) => (
                            <div
                                key={instance.instanceId}
                                className={ROW_CLASS}
                            >
                                <p className={LABEL_CLASS}>{widget.label.de}</p>
                                {/*
                                 * Every attribute with a concrete value, unlike
                                 * the embed snippet: the preview must render
                                 * exactly what the widget will look like,
                                 * defaults included. Rendered while still
                                 * loading: the entry only fetches a widget's
                                 * code once it finds the tag on the page.
                                 */}
                                {createElement(widget.tag, {
                                    ...buildAttributes(widget, config),
                                    [SAMPLE_DATA_ATTRIBUTE]: '',
                                })}
                            </div>
                        ))}
                    </>,
                    frame.document.body
                )}
        </>
    );
};
